import React, { useEffect, useMemo, useRef, useState } from "react";

export type HallScheme = {
    id: string;
    name: string;
    hall_id: string;
};

export type ToolKey =
    | "select"
    | "sector"
    | "horizontal-row"
    | "vertical-row"
    | "standing-place"
    | "text"
    | "shape";

export type ScaleValue = 1 | 0.8 | 0.6;

export type SchemeSector = {
    id: string;
    name: string;
    color: string;
};

export type SchemeSeat = {
    id: string;
    seatNumber: string;
    rowNumber: string;
    x: number;
    y: number;
    rowType: "horizontal" | "vertical";
    sectorId: string | null;
    rowGroupId: string;
};

export type SchemeStandingPlace = {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    text: string;
    sectorId: string | null;
};

export type SchemeTextLabel = {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    text: string;
};

export type SchemeShape = {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    shapeType: string;
    borderRadius: number;
    isOval: boolean;
};

type HallSchemeEditorCanvasProps = {
    tool: ToolKey;
    scale: ScaleValue;
    sectors: SchemeSector[];
    seats: SchemeSeat[];
    selectedSeatIds: string[];
    selectedSectorId: string | null;
    onSectorSelect: (sectorId: string | null) => void;
    onSeatsChange: (seats: SchemeSeat[]) => void;
    onSelectedSeatsChange: (seatIds: string[]) => void;
    standingPlaces: SchemeStandingPlace[];
    selectedStandingPlaceId: string | null;
    onStandingPlacesChange: (places: SchemeStandingPlace[]) => void;
    onStandingPlaceSelect: (placeId: string | null) => void;
    textLabels: SchemeTextLabel[];
    selectedTextLabelId: string | null;
    onTextLabelsChange: (items: SchemeTextLabel[]) => void;
    onTextLabelSelect: (id: string | null) => void;
    shapes: SchemeShape[];
    selectedShapeId: string | null;
    onShapesChange: (items: SchemeShape[]) => void;
    onShapeSelect: (id: string | null) => void;
};

const GRID_CELLS = 200;
const CELL_SIZE = 24;
const BOARD_SIZE = GRID_CELLS * CELL_SIZE;
const SEAT_SIZE = 18;

const createSeatId = () => `seat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const createRowGroupId = () => `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const createStandingPlaceId = () =>
    `standing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const createTextLabelId = () => `text-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const createShapeId = () => `shape-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const HallSchemeEditorCanvas: React.FC<HallSchemeEditorCanvasProps> = ({
                                                                           tool,
                                                                           scale,
                                                                           sectors,
                                                                           seats,
                                                                           selectedSeatIds,
                                                                           selectedSectorId,
                                                                           onSectorSelect,
                                                                           onSeatsChange,
                                                                           onSelectedSeatsChange,
                                                                           standingPlaces,
                                                                           selectedStandingPlaceId,
                                                                           onStandingPlacesChange,
                                                                           onStandingPlaceSelect,
                                                                           textLabels,
                                                                           selectedTextLabelId,
                                                                           onTextLabelsChange,
                                                                           onTextLabelSelect,
                                                                           shapes,
                                                                           selectedShapeId,
                                                                           onShapesChange,
                                                                           onShapeSelect,
                                                                       }) => {
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const gridSurfaceRef = useRef<HTMLDivElement | null>(null);

    const [isPointerDown, setIsPointerDown] = useState(false);
    const [dragStartCell, setDragStartCell] = useState<{ col: number; row: number } | null>(null);
    const [previewSeats, setPreviewSeats] = useState<SchemeSeat[]>([]);
    const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
    const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
    const [hoveredSeatId, setHoveredSeatId] = useState<string | null>(null);

    const [standingPlaceStartCell, setStandingPlaceStartCell] = useState<{ col: number; row: number } | null>(null);
    const [previewStandingPlace, setPreviewStandingPlace] = useState<SchemeStandingPlace | null>(null);
    const [draggingStandingPlaceId, setDraggingStandingPlaceId] = useState<string | null>(null);
    const [draggingStandingPlaceOffset, setDraggingStandingPlaceOffset] = useState<{ x: number; y: number } | null>(null);

    const [textStartCell, setTextStartCell] = useState<{ col: number; row: number } | null>(null);
    const [previewTextLabel, setPreviewTextLabel] = useState<SchemeTextLabel | null>(null);
    const [draggingTextLabelId, setDraggingTextLabelId] = useState<string | null>(null);
    const [draggingTextLabelOffset, setDraggingTextLabelOffset] = useState<{ x: number; y: number } | null>(null);

    const [shapeStartCell, setShapeStartCell] = useState<{ col: number; row: number } | null>(null);
    const [previewShape, setPreviewShape] = useState<SchemeShape | null>(null);
    const [draggingShapeId, setDraggingShapeId] = useState<string | null>(null);
    const [draggingShapeOffset, setDraggingShapeOffset] = useState<{ x: number; y: number } | null>(null);

    const gridStyle = useMemo(() => {
        return {
            width: `${BOARD_SIZE}px`,
            height: `${BOARD_SIZE}px`,
        };
    }, []);

    useEffect(() => {
        const area = scrollAreaRef.current;

        if (!area) {
            return;
        }

        const centerX = (BOARD_SIZE * scale - area.clientWidth) / 2;
        const centerY = (BOARD_SIZE * scale - area.clientHeight) / 2;

        area.scrollLeft = Math.max(0, centerX);
        area.scrollTop = Math.max(0, centerY);
    }, [scale]);


    const clearObjectSelections = () => {
        onSelectedSeatsChange([]);
        onStandingPlaceSelect(null);
        onTextLabelSelect(null);
        onShapeSelect(null);
    };

    const getPointerPosition = (event: React.PointerEvent<HTMLElement>) => {
        const surface = gridSurfaceRef.current;

        if (!surface) {
            return null;
        }

        const rect = surface.getBoundingClientRect();
        const x = (event.clientX - rect.left) / scale;
        const y = (event.clientY - rect.top) / scale;

        return {
            x: Math.max(0, Math.min(BOARD_SIZE, x)),
            y: Math.max(0, Math.min(BOARD_SIZE, y)),
        };
    };

    const getCellFromPointer = (event: React.PointerEvent<HTMLElement>) => {
        const point = getPointerPosition(event);

        if (!point) {
            return null;
        }

        const col = Math.max(0, Math.min(GRID_CELLS - 1, Math.floor(point.x / CELL_SIZE)));
        const row = Math.max(0, Math.min(GRID_CELLS - 1, Math.floor(point.y / CELL_SIZE)));

        return { col, row };
    };

    const buildAreaSeats = (
        start: { col: number; row: number },
        end: { col: number; row: number },
        rowType: "horizontal" | "vertical"
    ): SchemeSeat[] => {
        const generatedSeats: SchemeSeat[] = [];

        const minCol = Math.min(start.col, end.col);
        const maxCol = Math.max(start.col, end.col);
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);

        if (rowType === "horizontal") {
            const leftToRight = start.col <= end.col;
            let rowNumber = 1;

            for (let row = minRow; row <= maxRow; row += 1) {
                const rowGroupId = createRowGroupId();
                let seatNumber = 1;

                if (leftToRight) {
                    for (let col = minCol; col <= maxCol; col += 1) {
                        generatedSeats.push({
                            id: createSeatId(),
                            seatNumber: String(seatNumber),
                            rowNumber: String(rowNumber),
                            x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                            y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                            rowType,
                            sectorId: null,
                            rowGroupId,
                        });
                        seatNumber += 1;
                    }
                } else {
                    for (let col = maxCol; col >= minCol; col -= 1) {
                        generatedSeats.push({
                            id: createSeatId(),
                            seatNumber: String(seatNumber),
                            rowNumber: String(rowNumber),
                            x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                            y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                            rowType,
                            sectorId: null,
                            rowGroupId,
                        });
                        seatNumber += 1;
                    }
                }

                rowNumber += 1;
            }
        }

        if (rowType === "vertical") {
            const topToBottom = start.row <= end.row;
            const leftToRight = start.col <= end.col;

            let rowNumber = 1;

            if (leftToRight) {
                for (let col = minCol; col <= maxCol; col += 1) {
                    const rowGroupId = createRowGroupId();
                    let seatNumber = 1;

                    if (topToBottom) {
                        for (let row = minRow; row <= maxRow; row += 1) {
                            generatedSeats.push({
                                id: createSeatId(),
                                seatNumber: String(seatNumber),
                                rowNumber: String(rowNumber),
                                x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                rowType,
                                sectorId: null,
                                rowGroupId,
                            });
                            seatNumber += 1;
                        }
                    } else {
                        for (let row = maxRow; row >= minRow; row -= 1) {
                            generatedSeats.push({
                                id: createSeatId(),
                                seatNumber: String(seatNumber),
                                rowNumber: String(rowNumber),
                                x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                rowType,
                                sectorId: null,
                                rowGroupId,
                            });
                            seatNumber += 1;
                        }
                    }

                    rowNumber += 1;
                }
            } else {
                for (let col = maxCol; col >= minCol; col -= 1) {
                    const rowGroupId = createRowGroupId();
                    let seatNumber = 1;

                    if (topToBottom) {
                        for (let row = minRow; row <= maxRow; row += 1) {
                            generatedSeats.push({
                                id: createSeatId(),
                                seatNumber: String(seatNumber),
                                rowNumber: String(rowNumber),
                                x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                rowType,
                                sectorId: null,
                                rowGroupId,
                            });
                            seatNumber += 1;
                        }
                    } else {
                        for (let row = maxRow; row >= minRow; row -= 1) {
                            generatedSeats.push({
                                id: createSeatId(),
                                seatNumber: String(seatNumber),
                                rowNumber: String(rowNumber),
                                x: col * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                y: row * CELL_SIZE + (CELL_SIZE - SEAT_SIZE) / 2,
                                rowType,
                                sectorId: null,
                                rowGroupId,
                            });
                            seatNumber += 1;
                        }
                    }

                    rowNumber += 1;
                }
            }
        }

        return generatedSeats;
    };

    const buildStandingPlace = (
        start: { col: number; row: number },
        end: { col: number; row: number }
    ): SchemeStandingPlace => {
        const minCol = Math.min(start.col, end.col);
        const maxCol = Math.max(start.col, end.col);
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);

        return {
            id: createStandingPlaceId(),
            x: minCol * CELL_SIZE,
            y: minRow * CELL_SIZE,
            width: (maxCol - minCol + 1) * CELL_SIZE,
            height: (maxRow - minRow + 1) * CELL_SIZE,
            text: "Tanzfläche",
            sectorId: null,
        };
    };

    const buildTextLabel = (
        start: { col: number; row: number },
        end: { col: number; row: number }
    ): SchemeTextLabel => {
        const minCol = Math.min(start.col, end.col);
        const maxCol = Math.max(start.col, end.col);
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);

        return {
            id: createTextLabelId(),
            x: minCol * CELL_SIZE,
            y: minRow * CELL_SIZE,
            width: Math.max((maxCol - minCol + 1) * CELL_SIZE, CELL_SIZE * 2),
            height: Math.max((maxRow - minRow + 1) * CELL_SIZE, CELL_SIZE * 2),
            text: "Новый текст",
        };
    };

    const buildShape = (
        start: { col: number; row: number },
        end: { col: number; row: number }
    ): SchemeShape => {
        const minCol = Math.min(start.col, end.col);
        const maxCol = Math.max(start.col, end.col);
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);

        return {
            id: createShapeId(),
            x: minCol * CELL_SIZE,
            y: minRow * CELL_SIZE,
            width: Math.max((maxCol - minCol + 1) * CELL_SIZE, CELL_SIZE),
            height: Math.max((maxRow - minRow + 1) * CELL_SIZE, CELL_SIZE),
            shapeType: "rectangle",
            borderRadius: 12,
            isOval: false,
        };
    };

    const renumberSeatsPreservingGaps = (inputSeats: SchemeSeat[]) => {
        const groups = new Map<string, SchemeSeat[]>();

        inputSeats.forEach((seat) => {
            if (!groups.has(seat.rowGroupId)) {
                groups.set(seat.rowGroupId, []);
            }

            groups.get(seat.rowGroupId)?.push(seat);
        });

        const normalizedSeats: SchemeSeat[] = [];

        groups.forEach((groupSeats) => {
            if (groupSeats.length === 0) {
                return;
            }

            const axisSorted = [...groupSeats].sort((a, b) => {
                if (a.rowType === "horizontal") {
                    return a.x - b.x;
                }

                return a.y - b.y;
            });

            const firstSeat = axisSorted[0];
            const lastSeat = axisSorted[axisSorted.length - 1];

            const firstNumber = Number(firstSeat.seatNumber);
            const lastNumber = Number(lastSeat.seatNumber);

            const isForwardDirection = firstNumber <= lastNumber;
            const orderedSeats = isForwardDirection ? axisSorted : [...axisSorted].reverse();

            orderedSeats.forEach((seat, index) => {
                normalizedSeats.push({
                    ...seat,
                    seatNumber: String(index + 1),
                    rowNumber: seat.rowNumber,
                });
            });
        });

        return normalizedSeats;
    };

    const deleteSelectedSeats = () => {
        if (selectedSeatIds.length === 0) {
            return;
        }

        const nextSeats = seats.filter((seat) => !selectedSeatIds.includes(seat.id));
        const normalizedSeats = renumberSeatsPreservingGaps(nextSeats);

        onSeatsChange(normalizedSeats);
        onSelectedSeatsChange([]);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        const cell = getCellFromPointer(event);

        if (!cell) {
            return;
        }

        setIsPointerDown(true);

        if (tool === "horizontal-row" || tool === "vertical-row") {
            setDragStartCell(cell);
            setPreviewSeats([]);
        }

        if (tool === "standing-place") {
            const target = event.target as HTMLElement;

            if (target.closest(".scheme-editor-standing-place")) {
                return;
            }

            setStandingPlaceStartCell(cell);
            setPreviewStandingPlace(null);
        }

        if (tool === "text") {
            const target = event.target as HTMLElement;

            if (target.closest(".scheme-editor-text-label")) {
                return;
            }

            setTextStartCell(cell);
            setPreviewTextLabel(null);
        }

        if (tool === "shape") {
            const target = event.target as HTMLElement;

            if (target.closest(".scheme-editor-shape")) {
                return;
            }

            setShapeStartCell(cell);
            setPreviewShape(null);
        }

        if (tool === "select") {
            const target = event.target as HTMLElement;

            if (target.closest(".scheme-editor-seat-node")) {
                return;
            }

            const point = getPointerPosition(event);

            if (!point) {
                return;
            }

            onStandingPlaceSelect(null);
            onTextLabelSelect(null);
            onShapeSelect(null);
            setSelectionStart(point);
            setSelectionEnd(point);
        }
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (draggingStandingPlaceId && draggingStandingPlaceOffset) {
            const point = getPointerPosition(event);

            if (!point) {
                return;
            }

            const snappedX = Math.floor((point.x - draggingStandingPlaceOffset.x) / CELL_SIZE) * CELL_SIZE;
            const snappedY = Math.floor((point.y - draggingStandingPlaceOffset.y) / CELL_SIZE) * CELL_SIZE;

            onStandingPlacesChange(
                standingPlaces.map((item) =>
                    item.id === draggingStandingPlaceId
                        ? {
                            ...item,
                            x: Math.max(0, snappedX),
                            y: Math.max(0, snappedY),
                        }
                        : item
                )
            );

            return;
        }

        if (draggingTextLabelId && draggingTextLabelOffset) {
            const point = getPointerPosition(event);

            if (!point) {
                return;
            }

            const snappedX = Math.floor((point.x - draggingTextLabelOffset.x) / CELL_SIZE) * CELL_SIZE;
            const snappedY = Math.floor((point.y - draggingTextLabelOffset.y) / CELL_SIZE) * CELL_SIZE;

            onTextLabelsChange(
                textLabels.map((item) =>
                    item.id === draggingTextLabelId
                        ? {
                            ...item,
                            x: Math.max(0, snappedX),
                            y: Math.max(0, snappedY),
                        }
                        : item
                )
            );

            return;
        }

        if (draggingShapeId && draggingShapeOffset) {
            const point = getPointerPosition(event);

            if (!point) {
                return;
            }

            const snappedX = Math.floor((point.x - draggingShapeOffset.x) / CELL_SIZE) * CELL_SIZE;
            const snappedY = Math.floor((point.y - draggingShapeOffset.y) / CELL_SIZE) * CELL_SIZE;

            onShapesChange(
                shapes.map((item) =>
                    item.id === draggingShapeId
                        ? {
                            ...item,
                            x: Math.max(0, snappedX),
                            y: Math.max(0, snappedY),
                        }
                        : item
                )
            );

            return;
        }

        if (!isPointerDown) {
            return;
        }

        const cell = getCellFromPointer(event);

        if (!cell) {
            return;
        }

        if ((tool === "horizontal-row" || tool === "vertical-row") && dragStartCell) {
            const nextPreviewSeats = buildAreaSeats(
                dragStartCell,
                cell,
                tool === "horizontal-row" ? "horizontal" : "vertical"
            );

            setPreviewSeats(nextPreviewSeats);
        }

        if (tool === "standing-place" && standingPlaceStartCell) {
            setPreviewStandingPlace(buildStandingPlace(standingPlaceStartCell, cell));
        }

        if (tool === "text" && textStartCell) {
            setPreviewTextLabel(buildTextLabel(textStartCell, cell));
        }

        if (tool === "shape" && shapeStartCell) {
            setPreviewShape(buildShape(shapeStartCell, cell));
        }

        if (tool === "select" && selectionStart) {
            const point = getPointerPosition(event);

            if (!point) {
                return;
            }

            setSelectionEnd(point);
        }
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (draggingStandingPlaceId) {
            setDraggingStandingPlaceId(null);
            setDraggingStandingPlaceOffset(null);
            return;
        }

        if (draggingTextLabelId) {
            setDraggingTextLabelId(null);
            setDraggingTextLabelOffset(null);
            return;
        }

        if (draggingShapeId) {
            setDraggingShapeId(null);
            setDraggingShapeOffset(null);
            return;
        }

        const cell = getCellFromPointer(event);

        if (!cell) {
            setIsPointerDown(false);
            setPreviewSeats([]);
            setPreviewStandingPlace(null);
            setPreviewTextLabel(null);
            setPreviewShape(null);
            setDragStartCell(null);
            setStandingPlaceStartCell(null);
            setTextStartCell(null);
            setShapeStartCell(null);
            return;
        }

        if ((tool === "horizontal-row" || tool === "vertical-row") && dragStartCell) {
            const nextSeats = buildAreaSeats(
                dragStartCell,
                cell,
                tool === "horizontal-row" ? "horizontal" : "vertical"
            );

            onSeatsChange([...seats, ...nextSeats]);
            setPreviewSeats([]);
            setDragStartCell(null);
        }

        if (tool === "standing-place" && standingPlaceStartCell) {
            const nextStandingPlace = buildStandingPlace(standingPlaceStartCell, cell);
            onStandingPlacesChange([...standingPlaces, nextStandingPlace]);
            clearObjectSelections();
            onStandingPlaceSelect(nextStandingPlace.id);
            setStandingPlaceStartCell(null);
            setPreviewStandingPlace(null);
        }

        if (tool === "text" && textStartCell) {
            const nextTextLabel = buildTextLabel(textStartCell, cell);
            onTextLabelsChange([...textLabels, nextTextLabel]);
            clearObjectSelections();
            onTextLabelSelect(nextTextLabel.id);
            setTextStartCell(null);
            setPreviewTextLabel(null);
        }

        if (tool === "shape" && shapeStartCell) {
            const nextShape = buildShape(shapeStartCell, cell);
            onShapesChange([...shapes, nextShape]);
            clearObjectSelections();
            onShapeSelect(nextShape.id);
            setShapeStartCell(null);
            setPreviewShape(null);
        }

        if (tool === "select" && selectionStart) {
            const point = getPointerPosition(event);

            if (!point) {
                setSelectionStart(null);
                setSelectionEnd(null);
                setIsPointerDown(false);
                return;
            }

            const left = Math.min(selectionStart.x, point.x);
            const right = Math.max(selectionStart.x, point.x);
            const top = Math.min(selectionStart.y, point.y);
            const bottom = Math.max(selectionStart.y, point.y);

            const nextSelectedSeatIds = seats
                .filter((seat) => {
                    const seatCenterX = seat.x + SEAT_SIZE / 2;
                    const seatCenterY = seat.y + SEAT_SIZE / 2;

                    return (
                        seatCenterX >= left &&
                        seatCenterX <= right &&
                        seatCenterY >= top &&
                        seatCenterY <= bottom
                    );
                })
                .map((seat) => seat.id);

            onSelectedSeatsChange(nextSelectedSeatIds);
            setSelectionStart(null);
            setSelectionEnd(null);
        }

        setIsPointerDown(false);
    };

    const handleSeatClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        seatId: string
    ) => {
        if (tool !== "select") {
            return;
        }

        onStandingPlaceSelect(null);
        onTextLabelSelect(null);
        onShapeSelect(null);

        const isSelected = selectedSeatIds.includes(seatId);

        if (event.shiftKey) {
            if (isSelected) {
                onSelectedSeatsChange(selectedSeatIds.filter((id) => id !== seatId));
                return;
            }

            onSelectedSeatsChange([...selectedSeatIds, seatId]);
            return;
        }

        if (isSelected && selectedSeatIds.length === 1) {
            onSelectedSeatsChange([]);
            return;
        }

        onSelectedSeatsChange([seatId]);
    };

    const selectionRect = useMemo(() => {
        if (!selectionStart || !selectionEnd || tool !== "select") {
            return null;
        }

        return {
            left: Math.min(selectionStart.x, selectionEnd.x),
            top: Math.min(selectionStart.y, selectionEnd.y),
            width: Math.abs(selectionStart.x - selectionEnd.x),
            height: Math.abs(selectionStart.y - selectionEnd.y),
        };
    }, [selectionStart, selectionEnd, tool]);

    return (
        <div
            ref={scrollAreaRef}
            className="scheme-editor-canvas-area"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={() => {
                setIsPointerDown(false);
                setPreviewSeats([]);
                setPreviewStandingPlace(null);
                setPreviewTextLabel(null);
                setPreviewShape(null);
                setDragStartCell(null);
                setStandingPlaceStartCell(null);
                setTextStartCell(null);
                setShapeStartCell(null);
                setDraggingStandingPlaceId(null);
                setDraggingStandingPlaceOffset(null);
                setDraggingTextLabelId(null);
                setDraggingTextLabelOffset(null);
                setDraggingShapeId(null);
                setDraggingShapeOffset(null);
            }}
        >
            <div
                className="scheme-editor-grid-scale-layer"
                style={{ transform: `scale(${scale})` }}
            >
                <div
                    ref={gridSurfaceRef}
                    className="scheme-editor-grid-surface"
                    style={gridStyle}
                >
                    {shapes.map((shape) => {
                        const isSelected = selectedShapeId === shape.id;

                        return (
                            <button
                                key={shape.id}
                                type="button"
                                className={`scheme-editor-shape ${isSelected ? "scheme-editor-shape-active" : ""}`}
                                style={{
                                    left: `${shape.x}px`,
                                    top: `${shape.y}px`,
                                    width: `${shape.width}px`,
                                    height: `${shape.height}px`,
                                    borderRadius: shape.isOval ? "9999px" : `${shape.borderRadius}px`,
                                }}
                                onPointerDown={(event) => {
                                    event.stopPropagation();
                                    clearObjectSelections();

                                    if (tool === "select") {
                                        const point = getPointerPosition(event);

                                        if (!point) {
                                            return;
                                        }

                                        setDraggingShapeId(shape.id);
                                        setDraggingShapeOffset({
                                            x: point.x - shape.x,
                                            y: point.y - shape.y,
                                        });
                                        onShapeSelect(shape.id);
                                        return;
                                    }

                                    onShapeSelect(shape.id);
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    clearObjectSelections();
                                    onShapeSelect(shape.id);
                                }}
                                aria-label="Фигура"
                            />
                        );
                    })}

                    {previewShape ? (
                        <div
                            className="scheme-editor-shape scheme-editor-shape-preview"
                            style={{
                                left: `${previewShape.x}px`,
                                top: `${previewShape.y}px`,
                                width: `${previewShape.width}px`,
                                height: `${previewShape.height}px`,
                                borderRadius: previewShape.isOval ? "9999px" : `${previewShape.borderRadius}px`,
                            }}
                        />
                    ) : null}

                    {seats.map((seat) => {
                        const isSelected = selectedSeatIds.includes(seat.id);
                        const seatSector = sectors.find((sector) => sector.id === seat.sectorId);
                        const isHovered = hoveredSeatId === seat.id;

                        return (
                            <button
                                key={seat.id}
                                type="button"
                                className={`scheme-editor-seat-node ${isSelected ? "scheme-editor-seat-node-active" : ""}`}
                                style={{
                                    left: `${seat.x}px`,
                                    top: `${seat.y}px`,
                                    background: seatSector?.color ?? "#f8fafc",
                                    color: seatSector ? "#0f172a" : "#0f172a",
                                }}
                                onMouseEnter={() => setHoveredSeatId(seat.id)}
                                onMouseLeave={() => setHoveredSeatId(null)}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleSeatClick(event, seat.id);
                                }}
                            >
                                <span className="scheme-editor-seat-node-label">{seat.seatNumber}</span>

                                {isHovered ? (
                                    <span className="scheme-editor-seat-tooltip">
                                        <span>Место: {seat.seatNumber}</span>
                                        <span>Ряд: {seat.rowNumber}</span>
                                        {/*<span>*/}
                                        {/*    Тип: {seat.rowType === "horizontal" ? "горизонтальный" : "вертикальный"}*/}
                                        {/*</span>*/}
                                        <span>Сектор: {seatSector?.name ?? "не назначен"}</span>
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}

                    {previewSeats.map((seat) => (
                        <div
                            key={seat.id}
                            className="scheme-editor-seat-node scheme-editor-seat-node-preview"
                            style={{
                                left: `${seat.x}px`,
                                top: `${seat.y}px`,
                            }}
                        >
                            <span className="scheme-editor-seat-node-label">{seat.seatNumber}</span>
                        </div>
                    ))}

                    {standingPlaces.map((place) => {
                        const placeSector = sectors.find((sector) => sector.id === place.sectorId);
                        const isSelected = selectedStandingPlaceId === place.id;

                        return (
                            <button
                                key={place.id}
                                type="button"
                                className={`scheme-editor-standing-place ${isSelected ? "scheme-editor-standing-place-active" : ""}`}
                                style={{
                                    left: `${place.x}px`,
                                    top: `${place.y}px`,
                                    width: `${place.width}px`,
                                    height: `${place.height}px`,
                                    background: placeSector?.color ?? "rgba(248, 250, 252, 0.9)",
                                    color: placeSector ? "#ffffff" : "#0f172a",
                                }}
                                onPointerDown={(event) => {
                                    event.stopPropagation();
                                    onSelectedSeatsChange([]);
                                    onTextLabelSelect(null);
                                    onShapeSelect(null);

                                    if (tool === "select") {
                                        const point = getPointerPosition(event);

                                        if (!point) {
                                            return;
                                        }

                                        setDraggingStandingPlaceId(place.id);
                                        setDraggingStandingPlaceOffset({
                                            x: point.x - place.x,
                                            y: point.y - place.y,
                                        });
                                        onStandingPlaceSelect(place.id);
                                        return;
                                    }

                                    onStandingPlaceSelect(place.id);
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelectedSeatsChange([]);
                                    onTextLabelSelect(null);
                                    onShapeSelect(null);
                                    onStandingPlaceSelect(place.id);
                                }}
                            >
                                <span className="scheme-editor-standing-place-label">{place.text}</span>
                            </button>
                        );
                    })}

                    {previewStandingPlace ? (
                        <div
                            className="scheme-editor-standing-place scheme-editor-standing-place-preview"
                            style={{
                                left: `${previewStandingPlace.x}px`,
                                top: `${previewStandingPlace.y}px`,
                                width: `${previewStandingPlace.width}px`,
                                height: `${previewStandingPlace.height}px`,
                            }}
                        >
                            <span className="scheme-editor-standing-place-label">
                                {previewStandingPlace.text}
                            </span>
                        </div>
                    ) : null}

                    {textLabels.map((label) => {
                        const isSelected = selectedTextLabelId === label.id;

                        return (
                            <button
                                key={label.id}
                                type="button"
                                className={`scheme-editor-text-label ${isSelected ? "scheme-editor-text-label-active" : ""}`}
                                style={{
                                    left: `${label.x}px`,
                                    top: `${label.y}px`,
                                    width: `${label.width}px`,
                                    height: `${label.height}px`,
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    borderRadius: 0,
                                    boxShadow: isSelected ? "0 0 0 3px rgba(109, 124, 255, 0.3)" : "none",
                                    overflow: "visible",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                                onPointerDown={(event) => {
                                    event.stopPropagation();
                                    clearObjectSelections();

                                    if (tool === "select") {
                                        const point = getPointerPosition(event);

                                        if (!point) {
                                            return;
                                        }

                                        setDraggingTextLabelId(label.id);
                                        setDraggingTextLabelOffset({
                                            x: point.x - label.x,
                                            y: point.y - label.y,
                                        });
                                        onTextLabelSelect(label.id);
                                        return;
                                    }

                                    onTextLabelSelect(label.id);
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    clearObjectSelections();
                                    onTextLabelSelect(label.id);
                                }}
                            >
                                <span
                                    className="scheme-editor-text-label-text"
                                    style={{
                                        whiteSpace: "nowrap",
                                        background: "transparent",
                                        border: "none",
                                        display: "inline-block",
                                        textAlign: "center",
                                    }}
                                >
                                    {label.text}
                                </span>
                            </button>
                        );
                    })}

                    {previewTextLabel ? (
                        <div
                            className="scheme-editor-text-label scheme-editor-text-label-preview"
                            style={{
                                left: `${previewTextLabel.x}px`,
                                top: `${previewTextLabel.y}px`,
                                width: `${previewTextLabel.width}px`,
                                height: `${previewTextLabel.height}px`,
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                borderRadius: 0,
                                boxShadow: "none",
                                overflow: "visible",
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <span
                                className="scheme-editor-text-label-text"
                                style={{
                                    whiteSpace: "nowrap",
                                    background: "transparent",
                                    border: "none",
                                    display: "inline-block",
                                    textAlign: "left",
                                }}
                            >
                                {previewTextLabel.text}
                            </span>
                        </div>
                    ) : null}

                    {selectionRect ? (
                        <div
                            className="scheme-editor-selection-rect"
                            style={{
                                left: `${selectionRect.left}px`,
                                top: `${selectionRect.top}px`,
                                width: `${selectionRect.width}px`,
                                height: `${selectionRect.height}px`,
                            }}
                        />
                    ) : null}

                    {sectors.length === 0 &&
                    seats.length === 0 &&
                    standingPlaces.length === 0 &&
                    textLabels.length === 0 &&
                    shapes.length === 0 ? (
                        <div className="scheme-editor-grid-placeholder">
                            <div className="scheme-editor-grid-placeholder-badge">Рабочая область</div>
                            <h3 className="scheme-editor-grid-placeholder-title">
                                Центр схемы уже выровнен по центру области
                            </h3>
                            <p className="scheme-editor-grid-placeholder-text">
                                Сетка по умолчанию имеет размер 200 на 200 клеток. Добавьте сектор, начните рисовать ряды, создайте стоячее место, текст или фигуру.
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default HallSchemeEditorCanvas;
